import type {
  ConnectorInstallRecord,
  JsonObject,
  JsonValue,
  ProviderDescriptorField,
  ProviderDescriptorRecord,
  SecretRef,
} from "../../../../../packages/shared-types/src/index.js";
import {
  createSecretRef,
  formatSecretRefUri,
  listProviderCapabilityKeys,
} from "../../../../../packages/shared-types/src/index.js";

export type ProviderInstallFieldValues = Record<string, string>;
export type ProviderTargetFieldValues = Record<string, string>;

export type ParsedProviderInstallPayload =
  | { ok: true; credentialsRef: SecretRef; config: JsonObject }
  | { ok: false; fieldKey: string; message: string };

export type ParsedProviderTargetPayload =
  | {
      ok: true;
      targetKey: string;
      displayName: string;
      providerTargetRef: string;
      metadata: JsonObject;
    }
  | { ok: false; fieldKey: string; message: string };

export function getInstallableProviderDescriptors(
  descriptors: readonly ProviderDescriptorRecord[] | undefined,
): ProviderDescriptorRecord[] {
  return (descriptors ?? [])
    .filter((record) => record.executionHost === "runtime_connector" && record.connectorKey && record.install)
    .toSorted((left, right) => left.displayName.localeCompare(right.displayName));
}

export function getFirstPartyProviderDescriptors(
  descriptors: readonly ProviderDescriptorRecord[] | undefined,
): ProviderDescriptorRecord[] {
  return (descriptors ?? [])
    .filter((record) => record.executionHost === "control_plane_app")
    .toSorted((left, right) => left.displayName.localeCompare(right.displayName));
}

export function createProviderInstallFieldValues(
  descriptor: ProviderDescriptorRecord | undefined,
  install?: ConnectorInstallRecord,
): ProviderInstallFieldValues {
  const values: ProviderInstallFieldValues = {};
  for (const field of descriptor?.install?.fields ?? []) {
    values[field.key] = resolveInstallFieldValue(field, install);
  }
  return values;
}

export function createProviderTargetFieldValues(
  descriptor: ProviderDescriptorRecord | undefined,
  target?: {
    targetKey?: string;
    displayName?: string;
    providerTargetRef?: string;
    metadata?: JsonObject;
  },
): ProviderTargetFieldValues {
  const values: ProviderTargetFieldValues = {};
  for (const field of descriptor?.targets?.fields ?? []) {
    values[field.key] = resolveTargetFieldValue(field, target);
  }
  return values;
}

export function parseProviderInstallFieldValues(
  descriptor: ProviderDescriptorRecord | undefined,
  values: ProviderInstallFieldValues,
  options: {
    tenantId: string;
    existingInstall?: ConnectorInstallRecord;
  },
): ParsedProviderInstallPayload {
  if (!descriptor?.install) {
    return {
      ok: false,
      fieldKey: "descriptorKey",
      message: "Select an installable backend provider first.",
    };
  }

  let credentialsRef: SecretRef =
    options.existingInstall?.credentialsRef ??
    buildDefaultCredentialsRef(options.tenantId, descriptor.connectorKey ?? descriptor.providerKey);
  let config: JsonObject = {};
  for (const field of descriptor.install.fields) {
    const rawValue = values[field.key] ?? "";
    const trimmedValue = rawValue.trim();
    if (field.required && trimmedValue.length === 0) {
      return {
        ok: false,
        fieldKey: field.key,
        message: `${field.label} is required.`,
      };
    }

    if (field.type === "json") {
      const parsedJson = parseJsonField(field, rawValue);
      if (!parsedJson.ok) {
        return parsedJson;
      }
      if (field.key === "config") {
        config = parsedJson.value;
      } else if (Object.keys(parsedJson.value).length > 0) {
        config[field.key] = parsedJson.value;
      }
      continue;
    }

    if (trimmedValue.length > 0) {
      if (field.key === "workspaceApiKey") {
        config.runtimeCredentials = {
          ...(isJsonObject(config.runtimeCredentials) ? config.runtimeCredentials : {}),
          workspaceApiKey: trimmedValue,
        };
        continue;
      }
      config[field.key] = trimmedValue;
    }
  }

  return {
    ok: true,
    credentialsRef,
    config,
  };
}

export function parseProviderTargetFieldValues(
  descriptor: ProviderDescriptorRecord | undefined,
  values: ProviderTargetFieldValues,
): ParsedProviderTargetPayload {
  if (!descriptor?.targets?.fields?.length) {
    return {
      ok: false,
      fieldKey: "bindingId",
      message: "The selected backend does not declare target metadata.",
    };
  }

  let targetKey = "";
  let displayName = "";
  let providerTargetRef = "";
  let metadata: JsonObject = {};

  for (const field of descriptor.targets.fields) {
    const rawValue = values[field.key] ?? "";
    const trimmedValue = rawValue.trim();
    if (field.required && trimmedValue.length === 0) {
      return {
        ok: false,
        fieldKey: field.key,
        message: `${field.label} is required.`,
      };
    }
    if (field.key === "metadata") {
      const parsedJson = parseJsonField(field, rawValue);
      if (!parsedJson.ok) {
        return parsedJson;
      }
      metadata = parsedJson.value;
      continue;
    }
    if (field.key === "targetKey") {
      targetKey = trimmedValue;
      continue;
    }
    if (field.key === "displayName") {
      displayName = trimmedValue;
      continue;
    }
    if (field.key === "providerTargetRef") {
      providerTargetRef = trimmedValue;
      continue;
    }
    if (field.type === "json") {
      const parsedJson = parseJsonField(field, rawValue);
      if (!parsedJson.ok) {
        return parsedJson;
      }
      if (Object.keys(parsedJson.value).length > 0) {
        metadata[field.key] = parsedJson.value;
      }
      continue;
    }
    if (trimmedValue.length > 0) {
      metadata[field.key] = trimmedValue;
    }
  }

  if (!targetKey) {
    return {
      ok: false,
      fieldKey: "targetKey",
      message: "Target metadata must declare targetKey.",
    };
  }
  if (!displayName) {
    return {
      ok: false,
      fieldKey: "displayName",
      message: "Target metadata must declare displayName.",
    };
  }
  if (!providerTargetRef) {
    return {
      ok: false,
      fieldKey: "providerTargetRef",
      message: "Target metadata must declare providerTargetRef.",
    };
  }

  return {
    ok: true,
    targetKey,
    displayName,
    providerTargetRef,
    metadata,
  };
}

function buildDefaultCredentialsRef(tenantId: string, backendKey: string): SecretRef {
  return createSecretRef("control_plane", `${tenantId.trim()}/${backendKey.trim()}`);
}

export function listDescriptorCapabilityKeys(record: ProviderDescriptorRecord): string[] {
  return listProviderCapabilityKeys(record);
}

function resolveInstallFieldValue(
  field: ProviderDescriptorField,
  install: ConnectorInstallRecord | undefined,
): string {
  if (!install) {
    return defaultFieldValue(field);
  }

  if (field.key === "credentialsRef") {
    return install.credentialsRef ? formatSecretRefUri(install.credentialsRef) : "";
  }

  if (field.key === "config") {
    return JSON.stringify(install.config ?? {}, null, 2);
  }
  if (field.key === "workspaceApiKey") {
    const runtimeCredentials = isJsonObject(install.config?.runtimeCredentials)
      ? install.config.runtimeCredentials
      : undefined;
    const value = runtimeCredentials?.workspaceApiKey;
    return typeof value === "string" ? value : "";
  }

  const value = install.config?.[field.key];
  if (field.type === "json") {
    return JSON.stringify(value ?? field.defaultValue ?? {}, null, 2);
  }
  if (typeof value === "string") {
    return value;
  }
  return defaultFieldValue(field);
}

function resolveTargetFieldValue(
  field: ProviderDescriptorField,
  target:
    | {
        targetKey?: string;
        displayName?: string;
        providerTargetRef?: string;
        metadata?: JsonObject;
      }
    | undefined,
): string {
  if (!target) {
    return defaultFieldValue(field);
  }

  if (field.key === "metadata") {
    return JSON.stringify(target.metadata ?? field.defaultValue ?? {}, null, 2);
  }

  const value =
    field.key === "targetKey"
      ? target.targetKey
      : field.key === "displayName"
        ? target.displayName
        : field.key === "providerTargetRef"
          ? target.providerTargetRef
          : target.metadata?.[field.key];
  if (typeof value === "string") {
    return value;
  }
  if (field.type === "json" && isJsonObject(value)) {
    return JSON.stringify(value, null, 2);
  }
  return defaultFieldValue(field);
}

function defaultFieldValue(field: ProviderDescriptorField): string {
  if (field.defaultValue === undefined) {
    return field.type === "json" ? "{}" : "";
  }
  if (field.type === "json") {
    return JSON.stringify(field.defaultValue, null, 2);
  }
  return typeof field.defaultValue === "string" ? field.defaultValue : String(field.defaultValue);
}

function parseJsonField(
  field: ProviderDescriptorField,
  rawValue: string,
): { ok: true; value: JsonObject } | { ok: false; fieldKey: string; message: string } {
  const source = rawValue.trim().length > 0 ? rawValue : field.defaultValue !== undefined ? JSON.stringify(field.defaultValue) : "{}";
  try {
    const parsed = JSON.parse(source) as JsonValue;
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {
        ok: false,
        fieldKey: field.key,
        message: `${field.label} must be a JSON object.`,
      };
    }
    return { ok: true, value: parsed };
  } catch {
    return {
      ok: false,
      fieldKey: field.key,
      message: `${field.label} must be valid JSON.`,
    };
  }
}

function isJsonObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
